import { TableDeleteButton } from '@remirror/extension-react-tables';
import { TableBottomPopover, TableColumnRight, TableRowBelow } from './TableActionComponent';
import { useHelpers, useChainedCommands, useEditorEvent, useCommands } from '@remirror/react';
import {
  DeleteOutlined,
  BgColorsOutlined,
  DownOutlined,
  MergeCellsOutlined,
  SplitCellsOutlined,
} from '@ant-design/icons';
import { menuCellPositioner } from './../../utils/block-positioner';
import { PositionerPortal } from '@remirror/react-components';
import React, { useCallback, useState } from 'react';
import { ComponentsTheme } from '@remirror/theme';
import { usePositioner } from '@remirror/react';
import { Button, Menu } from 'antd';

export function useTableBackgroundColor(theme) {
  return [
    { color: 'var(--box-background-color)', isLight: theme === 'light' ? true : false },
    { color: '#758195', isLight: false },
    { color: '#1D7AFC', isLight: false },
    { color: '#289ABD', isLight: false },
    { color: '#22A06B', isLight: false },
    { color: '#FEA362', isLight: false },
    { color: '#C9372C', isLight: false },
    { color: '#8270DB', isLight: false },
  ];
}

const TableCellMenuPopup = ({ setPopupOpen, popupOpen, isLastMenuCell, theme }) => {
  const commands = useChainedCommands();
  const { mergeTableCells, splitTableCell } = useCommands();
  const colors = useTableBackgroundColor(theme);
  const handleMergeCells = useCallback(() => {
    mergeTableCells();
  }, []);
  const handleClick = useCallback((command) => {
    command().focus().run();
    setPopupOpen(false);
  }, []);
  const handleSplitCell = useCallback(() => {
    splitTableCell();
  }, []);

  return (
    <div
      style={{
        position: 'absolute',
        ...(isLastMenuCell && { right: 0 }),
        width: '200px',
        border: '1px solid var(--toolbar-button-hover)',
        display: popupOpen ? 'flex' : 'none',
        flexDirection: 'column',
      }}
    >
      <Menu className="table-menu" style={{ fontSize: '13px' }}>
        <Menu.SubMenu
          className="table-submenu-item"
          key="background"
          icon={<BgColorsOutlined />}
          title="Background Color"
        >
          <Menu.Item style={{ height: 'unset' }} key="colorPicker">
            <div className="color-picker-container">
              {colors.map((color, _) => (
                <div key={color.color} className="color-picker-item-wrapper">
                  <div
                    key={color.color}
                    className="color-picker-item"
                    style={{ backgroundColor: color.color }}
                    onClick={() => handleClick(() => commands.setTableCellBackground(color.color))}
                  ></div>
                </div>
              ))}
            </div>
          </Menu.Item>
        </Menu.SubMenu>
        <Menu.Divider />
        <Menu.Item
          className="table-menu-item"
          key="addColumnBelow"
          icon={<TableColumnRight />}
          onClick={() => handleClick(commands.addTableColumnAfter)}
        >
          Add column right
        </Menu.Item>
        <Menu.Item
          className="table-menu-item"
          key="addRowBelow"
          icon={<TableRowBelow />}
          onClick={() => handleClick(commands.addTableRowAfter)}
        >
          Add row below
        </Menu.Item>
        <Menu.Item
          className="table-menu-item"
          key="deleteColumn"
          icon={<DeleteOutlined />}
          onClick={() => handleClick(commands.deleteTableColumn)}
        >
          Delete Column
        </Menu.Item>
        <Menu.Item
          className="table-menu-item"
          key="deleteRow"
          icon={<DeleteOutlined />}
          onClick={() => handleClick(commands.deleteTableRow)}
        >
          Delete Row
        </Menu.Item>
        <Menu.Item
          className="table-menu-item"
          key="mergeCells"
          icon={<MergeCellsOutlined />}
          onClick={handleMergeCells}
          disabled={!mergeTableCells.enabled()}
        >
          Merge cells
        </Menu.Item>
        <Menu.Item
          className="table-menu-item"
          key="splitCell"
          icon={<SplitCellsOutlined />}
          onClick={handleSplitCell}
          disabled={!splitTableCell.enabled()}
        >
          Split Cell
        </Menu.Item>
      </Menu>
    </div>
  );
};

const TableCellMenuButton = ({ setPopupOpen }) => {
  return (
    <Button
      onClick={() => {
        setPopupOpen(true);
      }}
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      icon={<DownOutlined />}
      className={`${ComponentsTheme.BUTTON} table-cell-option-btn`}
    ></Button>
  );
};

const TableCellMenuComponent = ({ theme }) => {
  const position = usePositioner(menuCellPositioner, []);
  const [popupOpen, setPopupOpen] = useState(false);
  const { ref, width, height, x, y, data } = position;
  const isLastMenuCell = data?.isLastMenuCell ?? false;

  // Hide the popup when users click.
  useEditorEvent('mousedown', () => {
    if (popupOpen) {
      setPopupOpen(false);
    }

    return false;
  });

  return (
    <PositionerPortal>
      <div
        ref={ref}
        style={{
          position: 'absolute',
          left: x,
          top: y,
          width: width - 4,
          height: height - 4,
          zIndex: 2,
          pointerEvents: 'none',

          // place the child into the top-left corner
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',

          //   for debug:
          //   backgroundColor: 'lightpink',
          //   opacity: 0.5,
        }}
      >
        <div
          style={{
            zIndex: 100,
            pointerEvents: 'initial',
            // backgroundColor: 'lightcyan',
          }}
        >
          <TableCellMenuButton popupOpen={popupOpen} setPopupOpen={setPopupOpen} />
          <TableCellMenuPopup
            popupOpen={popupOpen}
            setPopupOpen={setPopupOpen}
            isLastMenuCell={isLastMenuCell}
            theme={theme}
          />
        </div>
      </div>
    </PositionerPortal>
  );
};

const TableComponents = ({
  enableTableCellMenu = true,
  enableTableDeleteRowColumnButton = true,
  enableTableDeleteButton = true,
  tableCellMenuProps,
  tableDeleteRowColumnButtonProps,
  tableDeleteButtonProps,
  theme = { theme },
}) => {
  const { isViewEditable } = useHelpers();

  if (!isViewEditable()) {
    return null;
  }

  return (
    <>
      {enableTableCellMenu && <TableCellMenuComponent {...tableCellMenuProps} theme={theme} />}
      {/* {enableTableDeleteRowColumnButton && <TableDeleteRowColumnButton {...tableDeleteRowColumnButtonProps} />} */}
      {enableTableDeleteButton && <TableDeleteButton Component={TableBottomPopover} />}
    </>
  );
};

export default TableComponents;
